import type {
  ActionRecord,
  ActivationCode,
  ActivationEnvironment,
  ActivationFeatureKey,
  AuditEvent,
  BusinessScenario,
  BusinessScenarioCapability,
  BusinessGoalPattern,
  BusinessScenarioVersion,
  CompanyBusinessProfile,
  CompanyBusinessProfileInput,
  Conversation,
  ConversationPage,
  ConversationRecordReference,
  ConversationTaskState,
  Message,
  MessagePage,
  ModelProfile,
  ModelProvider,
  ModelCatalog,
  SalesforceModelCredentialMigrationStatus,
  SalesforceModelCredentialStatus,
  KnowledgeCapabilityProbeResult,
  KnowledgeCapabilitySnapshot,
  GeneratedScenarioDraft,
  GenerationContextSnapshot,
  OrganizationConnection,
  OrganizationEntitlement,
  OrganizationDiff,
  OrganizationSnapshot,
  OrgAnalysisRun,
  ObjectCapabilitySummary,
  ObjectDirectoryEntry,
  OrgScanScope,
  PlatformAccountType,
  PlatformUser,
  UserProfile,
  AssistantProfile,
  AssistantPolicy,
  PlatformSystemStatus,
  PlatformRuntimeOverview,
  SalesforceUserBinding,
  UserEntitlement,
  SalesforceOAuthGrant,
  SalesforceContext,
  ScenarioActionBinding,
  ScenarioDiscoveryInput,
  ScenarioDiscoveryInputRequest,
  ScenarioMapping,
  ScenarioReadinessCheck,
  SchemaFingerprint,
  SkillCandidate,
  SkillManifest,
  TestDataset,
  TestDatasetVersion,
  TestRun,
  OperationsAuditEvent,
  OperationsCustomer,
  OperationsCustomerDetail,
  OperationsCustomerStatus,
  OperationsOverview,
  SalesforceEnvironmentProfileReadiness
} from "@ai-sales/contracts";

let csrfToken = "";
let sessionChannelHint: SessionChannel | "" = "";
let sessionRefreshPromise: Promise<SessionBootstrap> | undefined;

export type SessionChannel = "admin" | "salesforce" | "platform_web" | "website" | "development" | "ops";
export interface SessionBootstrap {
  csrfToken: string;
  context: Record<string, string>;
  channel: SessionChannel;
  platformUser?: PlatformUser;
  consentRequired?: boolean;
  operatorEmail?: string;
}

export interface SalesforceBusinessAccess {
  status: "ready" | "authorization_required" | "reauthorization_required" | "unavailable";
  ready: boolean;
  canSelfAuthorize: boolean;
  salesforceUserId?: string;
  reason?: string;
  grant?: SalesforceOAuthGrant;
}

export interface ActivationStartResult {
  authorizationUrl: string;
  expiresAt: string;
  target: {
    customerName: string;
    environment: ActivationEnvironment;
    environmentLabel: string;
    organizationId: string;
    loginOrigin: string;
  };
}

export interface ActivationContextResult {
  organizationId: string;
  salesforceUserId: string;
  environment: ActivationEnvironment;
  environmentLabel: string;
  loginOrigin: string;
  status: "not_activated" | "active" | "suspended" | "expired" | "revoked";
  isPrimaryAdmin: boolean;
}

export class ApiRequestError extends Error {
  readonly code?: string;
  readonly status: number;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

async function refreshSessionSecurity(): Promise<SessionBootstrap> {
  if (!sessionRefreshPromise) {
    sessionRefreshPromise = request<SessionBootstrap>("/api/session", {}, true)
      .then((result) => {
        csrfToken = result.csrfToken;
        sessionChannelHint = result.channel;
        return result;
      })
      .finally(() => {
        sessionRefreshPromise = undefined;
      });
  }
  return sessionRefreshPromise;
}

async function request<T>(path: string, init: RequestInit = {}, csrfRefreshAttempted = false): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...(sessionChannelHint ? { "X-AI-Session-Channel": sessionChannelHint } : {}),
      ...init.headers
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as {
      error?: string;
      code?: string;
      details?: string[];
    };
    const method = (init.method ?? "GET").toUpperCase();
    const csrfRejected = response.status === 403 && body.error === "CSRF 校验失败" &&
      ["POST", "PUT", "PATCH", "DELETE"].includes(method);
    if (csrfRejected && !csrfRefreshAttempted && path !== "/api/session") {
      try {
        await refreshSessionSecurity();
        return request<T>(path, init, true);
      } catch {
        throw new Error("安全会话已失效，请刷新 AI 助手后重新发送");
      }
    }
    const details = body.details?.filter(Boolean).slice(0, 3) ?? [];
    throw new ApiRequestError(
      [body.error ?? `HTTP ${response.status}`, ...details].join("："),
      response.status,
      body.code
    );
  }
  return response.json() as Promise<T>;
}

function isAmbiguousNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError ||
    (error instanceof Error && /Failed to fetch|NetworkError|网络|连接中断/i.test(error.message));
}

async function claimActionExecution(id: string): Promise<{
  claimed: true;
  idempotencyKey: string;
  replayed: boolean;
}> {
  const path = `/api/actions/${id}/claim-execution`;
  const init = { method: "POST", body: "{}" };
  try {
    return await request(path, init);
  } catch (error) {
    if (!isAmbiguousNetworkFailure(error)) throw error;
    return request(path, init);
  }
}

export const api = {
  setCsrf(value: string) { csrfToken = value; },
  setSessionChannelHint(value: SessionChannel) { sessionChannelHint = value; },
  async exchange(ticket: string, nonce: string, purpose: "assistant" | "admin" = "assistant") {
    const result = await request<SessionBootstrap>("/api/auth/salesforce/exchange", {
      method: "POST",
      body: JSON.stringify({ ticket, nonce, purpose })
    });
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  async createDevSession() {
    const result = await request<SessionBootstrap>("/api/dev/session", { method: "POST" });
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  startSalesforceSso: (returnPath = "/admin") => request<{ authorizationUrl: string; expiresAt: string }>(
    "/api/v1/auth/salesforce/sso/start",
    { method: "POST", body: JSON.stringify({ returnPath }) }
  ),
  getActivationContext: (ticket: string, nonce: string) =>
    request<ActivationContextResult>("/api/v1/activation/context", {
      method: "POST",
      body: JSON.stringify({ ticket, nonce })
    }),
  startActivation: (activationCode: string, ticket: string, nonce: string) =>
    request<ActivationStartResult>("/api/v1/activation/start", {
      method: "POST",
      body: JSON.stringify({ activationCode, ticket, nonce })
    }),
  requestOperationsEmailCode: (email: string) => request<{
    email: string;
    expiresInSeconds: number;
    resendInSeconds: number;
  }>("/api/v1/ops/auth/email/request", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  async verifyOperationsEmailCode(email: string, code: string) {
    const result = await request<SessionBootstrap>("/api/v1/ops/auth/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, code })
    });
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  getOperationsOverview: () => request<OperationsOverview>("/api/v1/ops/overview"),
  listOperationsCustomers: () => request<{ items: OperationsCustomer[] }>("/api/v1/ops/customers"),
  getOperationsCustomer: (customerId: string) => request<OperationsCustomerDetail>(
    `/api/v1/ops/customers/${encodeURIComponent(customerId)}`
  ),
  createOperationsCustomer: (input: {
    name: string;
    status: OperationsCustomerStatus;
    primaryContactName: string;
    contactEmail: string;
    contactPhone?: string;
    countryRegion: string;
    address?: string;
    contractStartDate?: string;
    contractEndDate?: string;
    note?: string;
  }) => request<OperationsCustomer>("/api/v1/ops/customers", {
    method: "POST",
    body: JSON.stringify(input)
  }),
  updateOperationsCustomer: (
    customerId: string,
    input: Partial<{
      name: string;
      status: OperationsCustomerStatus;
      primaryContactName: string;
      contactEmail: string;
      contactPhone: string;
      countryRegion: string;
      address: string;
      contractStartDate: string;
      contractEndDate: string;
      note: string;
    }> & { expectedVersion: number }
  ) => request<OperationsCustomer>(
    `/api/v1/ops/customers/${encodeURIComponent(customerId)}`,
    { method: "PATCH", body: JSON.stringify(input) }
  ),
  listActivationCodes: () => request<{ items: ActivationCode[] }>("/api/v1/ops/activation-codes"),
  listSalesforceEnvironmentProfiles: () => request<{ items: SalesforceEnvironmentProfileReadiness[] }>(
    "/api/v1/ops/salesforce-environments"
  ),
  updateSalesforceEnvironmentProfile: (
    profileId: string,
    input: {
      displayName: string;
      loginUrl: string;
      callbackUrl?: string | null;
      status: SalesforceEnvironmentProfileReadiness["profile"]["status"];
      note?: string | null;
      expectedVersion: number;
    }
  ) => request<SalesforceEnvironmentProfileReadiness>(
    `/api/v1/ops/salesforce-environments/${encodeURIComponent(profileId)}`,
    { method: "PATCH", body: JSON.stringify(input) }
  ),
  createActivationCode: (customerId: string, input: {
    expiresAt: string;
    seatLimit: number;
    featureKeys: ActivationFeatureKey[];
    note?: string;
    expectedOrganizationId: string;
    expectedSalesforceUserId: string;
  }) => request<{ activationCode: ActivationCode; plaintextCode: string }>(
    `/api/v1/ops/customers/${encodeURIComponent(customerId)}/activation-codes`,
    { method: "POST", body: JSON.stringify(input) }
  ),
  revokeActivationCode: (id: string) => request<ActivationCode>(
    `/api/v1/ops/activation-codes/${encodeURIComponent(id)}/revoke`,
    { method: "POST", body: "{}" }
  ),
  listOrganizationEntitlements: () => request<{ items: OrganizationEntitlement[] }>(
    "/api/v1/ops/organizations"
  ),
  updateOrganizationEntitlement: (
    organizationId: string,
    input: {
      status: OrganizationEntitlement["status"];
      expiresAt?: string | null;
      seatLimit?: number;
      featureKeys?: ActivationFeatureKey[];
      suspensionReason?: string | null;
      expectedVersion: number;
    }
  ) => request<OrganizationEntitlement>(
    `/api/v1/ops/organizations/${encodeURIComponent(organizationId)}`,
    { method: "PATCH", body: JSON.stringify(input) }
  ),
  listOperationsAuditEvents: () => request<{ items: OperationsAuditEvent[] }>(
    "/api/v1/ops/audit-events"
  ),
  requestPlatformEmailCode: (email: string, audience: "admin" | "workspace") => request<{
    email: string;
    expiresInSeconds: number;
    resendInSeconds: number;
  }>("/api/v1/auth/email/request", { method: "POST", body: JSON.stringify({ email, audience }) }),
  async verifyPlatformEmailCode(email: string, code: string, audience: "admin" | "workspace") {
    const result = await request<SessionBootstrap>("/api/v1/auth/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, code, audience })
    });
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  requestWebsiteEmailCode: (email: string) => request<{
    email: string;
    expiresInSeconds: number;
    resendInSeconds: number;
  }>("/api/auth/website/email/request", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  async verifyWebsiteEmailCode(email: string, code: string) {
    const result = await request<SessionBootstrap>("/api/auth/website/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, code })
    });
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  async resumeSession(channel?: SessionChannel) {
    if (channel) sessionChannelHint = channel;
    const result = await request<SessionBootstrap>("/api/session");
    csrfToken = result.csrfToken;
    sessionChannelHint = result.channel;
    return result;
  },
  logout: () => request<{ loggedOut: true; channel: SessionChannel }>("/api/v1/auth/logout", {
    method: "POST", body: "{}"
  }),
  logoutAll: () => request<{ loggedOut: true; revokedSessions: number }>("/api/v1/auth/logout-all", {
    method: "POST", body: "{}"
  }),
  getMyProfile: () => request<UserProfile>("/api/v1/me/profile"),
  updateMyProfile: (
    input: Pick<UserProfile, "nickname" | "description" | "avatarSource"> & { avatarUrl?: string }
  ) => request<UserProfile>("/api/v1/me/profile", {
    method: "PATCH", body: JSON.stringify(input)
  }),
  getAssistantProfile: () => request<AssistantProfile>("/api/v1/assistant-profile"),
  updateAssistantProfile: (
    input: Pick<AssistantProfile, "name" | "description" | "welcomeMessage"> & { avatarUrl?: string }
  ) => request<AssistantProfile>("/api/v1/admin/assistant-profile", {
    method: "PATCH", body: JSON.stringify(input)
  }),
  getAssistantPolicy: () => request<Pick<AssistantPolicy, "attachmentsEnabled" | "attachmentBatchWritesEnabled" | "resultExport"> & {
    objectExportPolicies: Record<string, "inherit" | "allow" | "deny">;
  }>("/api/v1/assistant-policy"),
  getAdminAssistantPolicy: () => request<Pick<AssistantPolicy, "attachmentsEnabled" | "attachmentBatchWritesEnabled" | "resultExport">>("/api/v1/admin/assistant-policy"),
  updateAssistantPolicy: (input: Partial<Pick<AssistantPolicy, "attachmentsEnabled" | "attachmentBatchWritesEnabled" | "resultExport">>) => request<AssistantPolicy>(
    "/api/v1/admin/assistant-policy",
    { method: "PATCH", body: JSON.stringify(input) }
  ),
  authorizeResultExport: (input: {
    actionId?: string;
    objectApiName: string;
    resultKind: "detail" | "grouped" | "chart";
    format: "excel" | "csv" | "png" | "svg";
    recordCount: number;
  }) => request<{ allowed: true }>("/api/v1/result-exports/authorize", {
    method: "POST", body: JSON.stringify(input)
  }),
  acceptEmbeddedConsent: () => request<{ accepted: true; termsVersion: string }>(
    "/api/v1/me/embedded-consent",
    { method: "POST", body: "{}" }
  ),
  listConversations: () => request<Conversation[]>("/api/conversations"),
  listConversationPage: (input: {
    status?: Conversation["status"][];
    search?: string;
    cursor?: string;
    limit?: number;
  } = {}) => {
    const query = new URLSearchParams();
    if (input.status?.length) query.set("status", input.status.join(","));
    if (input.search?.trim()) query.set("search", input.search.trim());
    if (input.cursor) query.set("cursor", input.cursor);
    query.set("limit", String(input.limit ?? 30));
    return request<ConversationPage>(`/api/v1/conversations?${query.toString()}`);
  },
  createConversation: (context: Record<string, unknown>, title = "AI 助手对话") => request<Conversation>("/api/conversations", {
    method: "POST",
    body: JSON.stringify({ title, context })
  }),
  createConversationV1: (context: Record<string, unknown>, title = "AI 助手对话") => request<Conversation>("/api/v1/conversations", {
    method: "POST",
    body: JSON.stringify({ title, context })
  }),
  updateConversation: (id: string, input: { title?: string; status?: "in_progress" | "completed" | "archived" }) =>
    request<Conversation>(`/api/v1/conversations/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  updateConversationContext: (id: string, context: Partial<Record<
    "recordId" | "recordLabel" | "objectApiName" |
    "accountId" | "accountLabel" | "contactId" | "contactLabel" |
    "opportunityId" | "opportunityLabel" | "leadId" | "leadLabel" | "appName" | "source",
    string | null
  >>) =>
    request<Conversation>(`/api/conversations/${id}/context`, { method: "PATCH", body: JSON.stringify(context) }),
  listMessages: (id: string) => request<Message[]>(`/api/conversations/${id}/messages`),
  listMessagePage: (id: string, input: { before?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (input.before) query.set("before", input.before);
    query.set("limit", String(input.limit ?? 30));
    return request<MessagePage>(`/api/v1/conversations/${id}/messages?${query.toString()}`);
  },
  getConversationState: (id: string) => request<ConversationTaskState>(`/api/v1/conversations/${id}/state`),
  listConversationReferences: (id: string) =>
    request<ConversationRecordReference[]>(`/api/v1/conversations/${id}/references`),
  sendMessage: (id: string, content: string, attachmentIds: string[] = []) => request<{
    userMessage: Message;
    assistantMessage: Message;
    assistantMessages?: Message[];
    action?: ActionRecord;
    actions?: ActionRecord[];
    model: string;
  }>(`/api/conversations/${id}/messages`, { method: "POST", body: JSON.stringify({ content, attachmentIds }) }),
  uploadAttachment: (conversationId: string, payload: { fileName: string; mimeType: string; dataBase64: string }) => request<{
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
  }>(`/api/conversations/${conversationId}/attachments`, { method: "POST", body: JSON.stringify(payload) }),
  prepareAttachmentUpload: (payload: {
    conversationId: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    sha256: string;
  }) => request<{
    attachment: {
      id: string;
      fileName: string;
      mimeType: string;
      size: number;
      status: "uploading";
    };
    upload: {
      url: string;
      headers: Record<string, string>;
      expiresAt: string;
    };
  }>("/api/v1/attachments/presign", { method: "POST", body: JSON.stringify(payload) }),
  uploadPreparedAttachment: async (url: string, headers: Record<string, string>, file: File) => {
    const response = await fetch(url, { method: "PUT", headers, body: file });
    if (!response.ok) throw new Error(`OSS 上传失败（HTTP ${response.status}）`);
  },
  completeAttachmentUpload: (attachmentId: string) => request<{
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
  }>(`/api/v1/attachments/${attachmentId}/complete`, { method: "POST", body: "{}" }),
  removeAttachment: (conversationId: string, attachmentId: string) => request<{ removed: boolean }>(
    `/api/conversations/${conversationId}/attachments/${attachmentId}`,
    { method: "DELETE", body: "{}" }
  ),
  transcribeVoice: (payload: { fileName: string; mimeType: string; dataBase64: string }) => request<{ text: string }>(
    "/api/voice/transcriptions",
    { method: "POST", body: JSON.stringify(payload) }
  ),
  getVoiceStatus: () => request<{
    available: boolean;
    source?: "managed_model" | "legacy";
    providerName?: string;
    model?: string;
    reason?: string;
  }>("/api/voice/status"),
  getAction: (id: string) => request<ActionRecord>(`/api/actions/${id}`),
  listActionCenter: () => request<ActionRecord[]>("/api/action-center"),
  reviseAction: (action: ActionRecord, input: { setFields: Record<string, unknown>; removeFields?: string[]; summary: string }) =>
    request<ActionRecord>(`/api/actions/${action.id}/draft`, {
      method: "PATCH",
      body: JSON.stringify({
        contentHash: action.contentHash,
        setFields: input.setFields,
        removeFields: input.removeFields ?? [],
        summary: input.summary
      })
    }),
  reviseBatchAction: (action: ActionRecord, batchEdits: Array<{
    sourceIndex: number;
    recordId?: string;
    setFields?: Record<string, unknown>;
    removeFields?: string[];
    excluded?: boolean;
  }>, summary: string) => request<ActionRecord>(`/api/actions/${action.id}/draft`, {
    method: "PATCH",
    body: JSON.stringify({
      contentHash: action.contentHash,
      setFields: {},
      removeFields: [],
      batchEdits,
      summary
    })
  }),
  retryFailedAction: (id: string) => request<ActionRecord>(`/api/actions/${id}/retry-failed`, {
    method: "POST",
    body: "{}"
  }),
  confirmAction: (action: ActionRecord) => request<ActionRecord>(`/api/actions/${action.id}/confirm`, {
    method: "POST",
    body: JSON.stringify({ contentHash: action.contentHash })
  }),
  reportActionPreview: (action: ActionRecord, body: { ok: boolean; result?: unknown; error?: string }) => request<ActionRecord>(`/api/actions/${action.id}/preview`, {
    method: "POST",
    body: JSON.stringify({ contentHash: action.contentHash, ...body })
  }),
  authorizeActionPreview: (action: ActionRecord) => request<{ authorized: true }>(
    `/api/actions/${action.id}/authorize-preview`,
    { method: "POST", body: JSON.stringify({ contentHash: action.contentHash }) }
  ),
  previewPlatformAction: (action: ActionRecord) => request<ActionRecord>(`/api/actions/${action.id}/preview-platform`, {
    method: "POST",
    body: JSON.stringify({ contentHash: action.contentHash })
  }),
  cancelAction: (id: string) => request<ActionRecord>(`/api/actions/${id}/cancel`, { method: "POST", body: "{}" }),
  reportActionResult: (id: string, payload: { ok: boolean; result?: unknown; error?: string }) => request<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    model: string;
    context?: Partial<SalesforceContext>;
  }>(`/api/actions/${id}/result`, {
    method: "POST",
    body: JSON.stringify(payload)
  }),
  claimActionExecution,
  executeWebsiteAction: (id: string) => request<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    model: string;
    context?: Partial<SalesforceContext>;
  }>(`/api/actions/${id}/execute-website`, { method: "POST", body: "{}" }),
  executePlatformAction: (id: string) => request<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    model: string;
    context?: Partial<SalesforceContext>;
  }>(`/api/actions/${id}/execute-platform`, { method: "POST", body: "{}" }),
  executeSalesforceReadAction: (id: string) => request<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    model: string;
    context?: Partial<SalesforceContext>;
  }>(`/api/actions/${id}/execute-salesforce-read`, { method: "POST", body: "{}" }),
  executeDemoAction: (id: string) => request<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    model: string;
    context?: Partial<SalesforceContext>;
  }>(`/api/actions/${id}/execute-demo`, { method: "POST", body: "{}" }),
  getAdminIdentity: () => request<{ user: PlatformUser; organizationId: string }>("/api/v1/admin/me"),
  getCompanyProfile: () => request<CompanyBusinessProfile | null>("/api/company-profile"),
  listCompanyProfileVersions: () => request<CompanyBusinessProfile[]>("/api/v1/admin/company-profile/versions"),
  saveCompanyProfile: (input: CompanyBusinessProfileInput) => request<CompanyBusinessProfile>(
    "/api/company-profile",
    { method: "PUT", body: JSON.stringify(input) }
  ),
  getSystemSettings: () => request<PlatformSystemStatus>("/api/v1/admin/system-settings"),
  getRuntimeOverview: () => request<PlatformRuntimeOverview>("/api/v1/admin/runtime-overview"),
  listUsers: () => request<PlatformUser[]>("/api/v1/admin/users"),
  createUser: (input: {
    email: string;
    displayName: string;
    accountType: PlatformAccountType;
  }) => request<PlatformUser>(
    "/api/v1/admin/users/invitations",
    { method: "POST", body: JSON.stringify(input) }
  ),
  updateUser: (
    id: string,
    input: Pick<PlatformUser, "status"> & { accountType: PlatformAccountType }
  ) => request<PlatformUser>(`/api/v1/admin/users/${id}`, {
    method: "PATCH", body: JSON.stringify(input)
  }),
  getUserAccess: (id: string) => request<{
    userId: string;
    binding?: SalesforceUserBinding;
    entitlement?: UserEntitlement;
  }>(`/api/v1/admin/users/${id}/access`),
  updateUserSalesforceBinding: (
    id: string,
    input: { salesforceUserId: string; status?: "active" | "disabled" }
  ) => request<SalesforceUserBinding>(`/api/v1/admin/users/${id}/salesforce-binding`, {
    method: "PUT", body: JSON.stringify(input)
  }),
  updateUserEntitlement: (
    id: string,
    input: Pick<UserEntitlement, "status" | "scenarioIds" | "roleKeys"> &
      Partial<Pick<UserEntitlement, "startsAt" | "expiresAt" | "usageLimit">>
  ) => request<UserEntitlement>(`/api/v1/admin/users/${id}/entitlement`, {
    method: "PUT", body: JSON.stringify(input)
  }),
  listSalesforceGrants: () => request<SalesforceOAuthGrant[]>("/api/v1/auth/salesforce/grants"),
  getSalesforceBusinessAccess: () => request<SalesforceBusinessAccess>("/api/v1/auth/salesforce/business-access"),
  startSalesforceGrant: (purpose: "org_management" | "business_data", returnPath = "/admin") => request<{
    authorizationUrl: string;
    expiresAt: string;
  }>("/api/v1/auth/salesforce/oauth/start", { method: "POST", body: JSON.stringify({ purpose, returnPath }) }),
  revokeSalesforceGrant: (id: string) => request<SalesforceOAuthGrant>(`/api/v1/auth/salesforce/grants/${id}`, {
    method: "DELETE", body: "{}"
  }),
  getOrgConnection: () => request<OrganizationConnection | undefined>("/api/v1/admin/org-connections/current"),
  verifyOrgConnection: () => request<{ connection: OrganizationConnection; authorizationSource: "admin_oauth" | "integration_user"; organization: { id: string; name: string; instanceName?: string | null; isSandbox: boolean } }>("/api/v1/admin/org-connections/current/verify", { method: "POST", body: "{}" }),
  updateOrgConnection: (input: {
    cloudVariant?: OrganizationConnection["cloudVariant"];
    loginUrl?: string;
    myDomainUrl?: string;
    businessPermissionSetApiNames?: string[];
  }) => request<OrganizationConnection>("/api/v1/admin/org-connections/current", {
    method: "PATCH",
    body: JSON.stringify(input)
  }),
  updateOrgConnectionPermissionSets: (businessPermissionSetApiNames: string[]) => request<OrganizationConnection>("/api/v1/admin/org-connections/current", {
    method: "PATCH",
    body: JSON.stringify({ businessPermissionSetApiNames })
  }),
  listObjectDirectory: () => request<ObjectDirectoryEntry[]>("/api/org-analysis/directory", {
    method: "POST",
    body: "{}"
  }),
  listOrgScanScope: () => request<OrgScanScope[]>("/api/v1/admin/org-analysis/scope"),
  updateOrgScanScope: (objectApiName: string, input: {
    enabled: boolean;
    maintenanceState: OrgScanScope["maintenanceState"];
    operations?: OrgScanScope["operations"];
    permissionSetApiNames?: string[];
    exportPolicy?: OrgScanScope["exportPolicy"];
  }) => request<OrgScanScope>(
    `/api/v1/admin/org-analysis/scope/${encodeURIComponent(objectApiName)}`,
    { method: "PUT", body: JSON.stringify(input) }
  ),
  updateObjectExportPolicy: (objectApiName: string, exportPolicy: NonNullable<OrgScanScope["exportPolicy"]>) => request<OrgScanScope>(
    `/api/v1/admin/org-analysis/scope/${encodeURIComponent(objectApiName)}/export-policy`,
    { method: "PATCH", body: JSON.stringify({ exportPolicy }) }
  ),
  saveOrgSelections: (selections: Array<{
    objectApiName: string;
    operations: OrgScanScope["operations"];
    permissionSetApiNames?: string[];
    exportPolicy?: OrgScanScope["exportPolicy"];
  }>) => request<OrgScanScope[]>("/api/org-selections", {
    method: "PUT",
    body: JSON.stringify({ selections })
  }),
  runOrgScan: (trigger: OrganizationSnapshot["trigger"] = "manual") => request<{
    snapshot: OrganizationSnapshot;
    diff?: OrganizationDiff;
    candidates: SkillCandidate[];
    run: OrgAnalysisRun;
    fingerprint: SchemaFingerprint;
  }>("/api/org-analysis/selected-objects", { method: "POST", body: JSON.stringify({ trigger }) }),
  listOrgSnapshots: () => request<OrganizationSnapshot[]>("/api/v1/admin/org-analysis/snapshots"),
  listOrgAnalysisRuns: () => request<OrgAnalysisRun[]>("/api/v1/admin/org-analysis/runs"),
  getOrgAnalysisRun: (id: string) => request<OrgAnalysisRun>(`/api/org-analysis/runs/${encodeURIComponent(id)}`),
  listSchemaFingerprints: () => request<SchemaFingerprint[]>("/api/v1/admin/schema-fingerprints"),
  listOrgDiffs: () => request<OrganizationDiff[]>("/api/v1/admin/org-analysis/diffs"),
  listKnowledgeCapabilities: () => request<KnowledgeCapabilitySnapshot[]>("/api/v1/admin/knowledge/capabilities"),
  probeKnowledgeCapabilities: () => request<KnowledgeCapabilityProbeResult>(
    "/api/knowledge/capabilities",
    { method: "POST", body: "{}" }
  ),
  listSkillCandidates: () => request<SkillCandidate[]>("/api/v1/admin/skill-candidates"),
  acceptSkillCandidate: (id: string) => request<SkillManifest>(`/api/v1/admin/skill-candidates/${id}/accept`, { method: "POST", body: "{}" }),
  listObjectCapabilities: () => request<ObjectCapabilitySummary[]>("/api/v1/admin/object-capabilities"),
  listScenarioPatterns: () => request<BusinessGoalPattern[]>("/api/v1/admin/scenario-patterns"),
  listScenarioDiscoveryInputs: () => request<ScenarioDiscoveryInput[]>("/api/v1/admin/scenario-discovery"),
  listScenarioDrafts: () => request<Array<{
    draft: GeneratedScenarioDraft;
    mappings: ScenarioMapping[];
    context?: GenerationContextSnapshot;
  }>>("/api/v1/admin/scenario-drafts"),
  createScenarioDiscoveryInput: (input: ScenarioDiscoveryInputRequest) =>
    request<ScenarioDiscoveryInput>("/api/v1/admin/scenario-discovery", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  generateScenarioDraft: (discoveryInputId: string) => request<{
    draft: GeneratedScenarioDraft;
    mappings: ScenarioMapping[];
    context: GenerationContextSnapshot;
  }>(`/api/v1/admin/scenario-discovery/${encodeURIComponent(discoveryInputId)}/generate`, {
    method: "POST",
    body: "{}"
  }),
  getScenarioDraft: (id: string) => request<{
    draft: GeneratedScenarioDraft;
    mappings: ScenarioMapping[];
    context: GenerationContextSnapshot;
  }>(`/api/v1/admin/scenario-drafts/${encodeURIComponent(id)}`),
  confirmScenarioMappings: (draftId: string, confirmations: Array<{
    id: string;
    objectApiName?: string | null;
    fieldApiName?: string | null;
    targetObjectApiName?: string | null;
    recordTypeDeveloperName?: string | null;
    picklistValue?: string | null;
    actionKey?: string | null;
    operation?: "query" | "aggregate" | "create" | "update" | "delete" | null;
    status: "confirmed" | "unresolved" | "excluded";
  }>) => request<{ draft: GeneratedScenarioDraft; mappings: ScenarioMapping[] }>(
    `/api/v1/admin/scenario-drafts/${encodeURIComponent(draftId)}/mappings`,
    { method: "PUT", body: JSON.stringify({ confirmations }) }
  ),
  compileScenarioDraft: (draftId: string, existingScenarioId?: string) => request<{
    scenario: BusinessScenario;
    scenarioVersion: BusinessScenarioVersion;
    draft: GeneratedScenarioDraft;
    actionBindings: ScenarioActionBinding[];
    readiness: ScenarioReadinessCheck;
  }>(`/api/v1/admin/scenario-drafts/${encodeURIComponent(draftId)}/compile`, {
    method: "POST",
    body: JSON.stringify(existingScenarioId ? { existingScenarioId } : {})
  }),
  listBusinessScenarios: () => request<BusinessScenario[]>("/api/v1/admin/business-scenarios"),
  createBusinessScenario: (input: {
    name: string;
    businessGoal: string;
    exampleRequests: string[];
    capabilities: BusinessScenarioCapability[];
  }) => request<BusinessScenario>("/api/v1/admin/business-scenarios", { method: "POST", body: JSON.stringify(input) }),
  updateBusinessScenario: (id: string, input: {
    name: string;
    businessGoal: string;
    exampleRequests: string[];
    capabilities: BusinessScenarioCapability[];
  }) => request<BusinessScenario>(`/api/v1/admin/business-scenarios/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  disableBusinessScenario: (id: string) => request<BusinessScenario>(`/api/v1/admin/business-scenarios/${id}/disable`, { method: "POST", body: "{}" }),
  enableBusinessScenario: (id: string) => request<BusinessScenario>(`/api/v1/admin/business-scenarios/${id}/enable`, { method: "POST", body: "{}" }),
  publishBusinessScenario: (id: string, assignToAllActiveUsers = true) => request<{ scenario: BusinessScenario; assignedUserCount: number }>(
    `/api/v1/admin/business-scenarios/${id}/publish`,
    { method: "POST", body: JSON.stringify({ assignToAllActiveUsers }) }
  ),
  listSkills: () => request<SkillManifest[]>("/api/v1/admin/skills"),
  saveSkill: (skill: SkillManifest) => request<SkillManifest>("/api/v1/admin/skills", { method: "POST", body: JSON.stringify(skill) }),
  listSkillVersions: (key: string) => request<SkillManifest[]>(`/api/v1/admin/skills/${encodeURIComponent(key)}/versions`),
  listTestDatasets: (skillKey?: string) => request<TestDataset[]>(`/api/v1/admin/test-datasets${skillKey ? `?skillKey=${encodeURIComponent(skillKey)}` : ""}`),
  listTestDatasetVersions: (id: string) => request<TestDatasetVersion[]>(`/api/v1/admin/test-datasets/${encodeURIComponent(id)}/versions`),
  testSkill: (key: string) => request<TestRun>(`/api/v1/admin/skills/${encodeURIComponent(key)}/test-runs`, { method: "POST", body: "{}" }),
  startSkillTrial: (key: string) => request<SkillManifest>(`/api/v1/admin/skills/${encodeURIComponent(key)}/trial`, { method: "POST", body: "{}" }),
  publishSkill: (key: string) => request<SkillManifest>(`/api/v1/admin/skills/${encodeURIComponent(key)}/publish`, { method: "POST", body: "{}" }),
  rollbackSkill: (key: string, version: string) => request<SkillManifest>(`/api/v1/admin/skills/${encodeURIComponent(key)}/rollback`, {
    method: "POST", body: JSON.stringify({ version })
  }),
  listTestRuns: (skillKey?: string) => request<TestRun[]>(`/api/v1/admin/test-runs${skillKey ? `?skillKey=${encodeURIComponent(skillKey)}` : ""}`),
  listModelProviders: () => request<ModelProvider[]>("/api/v1/admin/models/providers"),
  listModelProfiles: () => request<ModelProfile[]>("/api/v1/admin/models/profiles"),
  discoverModelCatalog: (input: {
    kind: ModelProvider["kind"];
    protocol: ModelProvider["protocol"];
    baseUrl: string;
    authMode: "bearer" | "api-key";
    apiKey?: string;
    connectionProviderId?: string;
    role: NonNullable<ModelProfile["role"]>;
  }) => request<ModelCatalog>("/api/v1/admin/models/catalog", { method: "POST", body: JSON.stringify(input) }),
  getSalesforceModelCredentialMigrationStatus: () => request<SalesforceModelCredentialMigrationStatus>("/api/v1/admin/models/salesforce-credential/migration-status"),
  revokeSalesforceModelCredential: () => request<SalesforceModelCredentialStatus>("/api/v1/admin/models/salesforce-credential", {
    method: "DELETE", body: "{}"
  }),
  createModelProvider: (input: {
    name: string;
    kind: ModelProvider["kind"];
    protocol: ModelProvider["protocol"];
    baseUrl: string;
    authMode: "bearer" | "api-key";
    apiKey?: string;
    connectionProviderId?: string;
    model: string;
    profileName: string;
    role: NonNullable<ModelProfile["role"]>;
    maxContextTokens?: number;
    maxOutputTokens?: number;
    speechProbe?: {
      fileName: string;
      mimeType: "audio/webm" | "audio/ogg" | "audio/mp4" | "audio/mpeg" | "audio/wav";
      dataBase64: string;
    };
  }) => request<{
    provider: ModelProvider;
    profile: ModelProfile;
    operation: "created" | "replaced";
    retiredProviderCount: number;
  }>("/api/v1/admin/models/providers", { method: "POST", body: JSON.stringify(input) }),
  verifyModelProvider: (id: string) => request<ModelProvider>(`/api/v1/admin/models/providers/${id}/verify`, { method: "POST", body: "{}" }),
  listAuditEvents: () => request<AuditEvent[]>("/api/v1/admin/audit-events")
};
